/**
 * <kallo-slider> — one slide at a time, arrows either side, dots beneath.
 *
 * Like the belt gallery, the stage is a scroll-snap strip, so a swipe is the
 * browser's own behaviour and the slides can still be scrolled with this file
 * absent. This adds the arrows and dots, and keeps them in step with the
 * scroll position rather than with whichever control was used last.
 *
 * The arrows wrap: past the last material is the first again.
 */
(function () {
  'use strict';

  class KalloSlider extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.stage = this.querySelector('[data-slider-stage]');
      this.slides = Array.from(this.querySelectorAll('[data-slider-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));

      if (!this.stage || this.slides.length < 2) return;

      this.index = 0;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-slider-prev]')) return this.go(this.index - 1);
        if (event.target.closest('[data-slider-next]')) return this.go(this.index + 1);
        const dot = event.target.closest('[data-slider-dot]');
        if (dot) this.go(Number(dot.dataset.sliderDot));
      });

      this.stage.addEventListener('scroll', () => this.queueSync(), { passive: true });
      this.sync();
    }

    go(index) {
      const count = this.slides.length;
      const target = ((index % count) + count) % count;
      // `scrollLeft` rather than a smooth `scrollTo`: mandatory snapping
      // cancels a scripted smooth scroll. The CSS `scroll-behavior` animates it.
      this.stage.scrollLeft = this.slides[target].offsetLeft - this.slides[0].offsetLeft;
      this.index = target;
      this.sync();
    }

    queueSync() {
      if (this.queued) return;
      this.queued = true;
      window.requestAnimationFrame(() => {
        this.queued = false;
        const at = Math.round(this.stage.scrollLeft / (this.stage.clientWidth || 1));
        const clamped = Math.max(0, Math.min(at, this.slides.length - 1));
        if (clamped !== this.index) {
          this.index = clamped;
          this.sync();
        }
      });
    }

    sync() {
      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
      // `aria-hidden` rather than `inert`, so nothing about the slides changes
      // for layout or snapping while the strip is moving. They hold nothing
      // focusable, so hiding them from assistive tech is enough.
      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', i === this.index ? 'false' : 'true');
      });
    }
  }

  if (!customElements.get('kallo-slider')) {
    customElements.define('kallo-slider', KalloSlider);
  }
})();
